<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  knowledgebaseId: {
    type: String,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const isDownloading = ref(false);

const records = computed(() => store.getters['records/getRecords']);
const pagination = computed(() => store.getters['records/getPagination']);
const uiFlags = computed(() => store.getters['records/getUIFlags']);

async function fetchRecords() {
  if (isLoading.value) return;
  isLoading.value = true;
  try {
    await store.dispatch('records/get', props.knowledgebaseId);
  } finally {
    isLoading.value = false;
  }
}

async function loadMore() {
  await store.dispatch('records/loadMore', props.knowledgebaseId);
}

async function fetchAll() {
  await fetchRecords();
  while (pagination.value.hasMore && !uiFlags.value.isLoadingMore) {
    // eslint-disable-next-line no-await-in-loop
    await store.dispatch('records/loadMore', props.knowledgebaseId);
  }
}

function downloadAs(format) {
  isDownloading.value = true;
  try {
    const data = records.value.map(r => ({
      id: r.id,
      name: r.file ? r.file.filename : r.source,
      type: r.file ? r.file.extension : 'URL',
      status: r.status,
      created_at: r.created_at,
      updated_at: r.updated_at,
    }));

    let blob;
    let filename;

    if (format === 'json') {
      blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      filename = `records-${props.knowledgebaseId}.json`;
    } else {
      const headers = [
        'id',
        'name',
        'type',
        'status',
        'created_at',
        'updated_at',
      ];
      const rows = data.map(row =>
        headers.map(h => JSON.stringify(row[h] ?? '')).join(',')
      );
      blob = new Blob([[headers.join(','), ...rows].join('\n')], {
        type: 'text/csv',
      });
      filename = `records-${props.knowledgebaseId}.csv`;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } finally {
    isDownloading.value = false;
  }
}

async function handleDownloadAll(format) {
  isDownloading.value = true;
  try {
    await fetchAll();
    downloadAs(format);
  } finally {
    isDownloading.value = false;
  }
}

onMounted(fetchRecords);
onUnmounted(() => store.dispatch('records/clearRecords'));
</script>

<template>
  <div class="flex-1 !overflow-auto h-full pt-6 p-8">
    <h2 class="text-xl font-semibold text-neutral-900 dark:text-white mb-4">
      {{ $t('LIBRARIES.RECORDS_TOOL.TITLE') }}
    </h2>

    <div class="flex items-center gap-2 mb-4">
      <NextButton
        icon="i-lucide-refresh-ccw"
        :label="$t('LIBRARIES.RECORDS_TOOL.REFRESH')"
        md
        slate
        :is-loading="isLoading"
        @click="fetchRecords"
      />
      <NextButton
        icon="i-lucide-download"
        :label="$t('LIBRARIES.RECORDS_TOOL.DOWNLOAD_JSON')"
        md
        slate
        :is-loading="isDownloading"
        @click="handleDownloadAll('json')"
      />
      <NextButton
        icon="i-lucide-file-spreadsheet"
        :label="$t('LIBRARIES.RECORDS_TOOL.DOWNLOAD_CSV')"
        md
        slate
        :is-loading="isDownloading"
        @click="handleDownloadAll('csv')"
      />
      <span class="text-sm text-neutral-500 ml-auto">
        {{
          $t('LIBRARIES.RECORDS_TOOL.COUNT', { count: pagination.totalCount })
        }}
      </span>
    </div>

    <div v-if="isLoading && records.length === 0" class="text-neutral-500 py-4">
      {{ $t('LIBRARIES.TOOLS.LOADING') }}
    </div>

    <table v-else class="w-full woot-table mb-4">
      <thead class="border-b border-neutral-100">
        <tr>
          <th class="text-left w-16">
            {{ $t('LIBRARIES.RECORDS.ID.LABEL') }}
          </th>
          <th class="text-left">
            {{ $t('LIBRARIES.RECORDS.FILE_NAME.LABEL') }}
          </th>
          <th class="text-left w-24">
            {{ $t('LIBRARIES.RECORDS.FILE_TYPE.LABEL') }}
          </th>
          <th class="text-left w-28">
            {{ $t('LIBRARIES.RECORDS.STATUS.LABEL') }}
          </th>
          <th class="text-left w-44">
            {{ $t('LIBRARIES.RECORDS.UPDATED_AT.LABEL') }}
          </th>
          <th class="w-12" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="record in records"
          :key="record.id"
          class="border-b border-neutral-100"
        >
          <td class="text-neutral-500 text-sm">{{ record.id }}</td>
          <td>
            <span class="inline-block max-w-full break-all text-sm">
              {{ record.file ? record.file.filename : record.source }}
            </span>
          </td>
          <td class="uppercase text-sm text-neutral-600">
            {{ record.file ? record.file.extension : 'URL' }}
          </td>
          <td>
            <span
              class="px-2 py-0.5 rounded text-xs font-medium bg-neutral-100 text-neutral-700"
            >
              {{ record.status }}
            </span>
          </td>
          <td class="text-sm text-neutral-500">
            {{ new Date(record.updated_at).toLocaleString() }}
          </td>
          <td class="text-right">
            <a
              v-if="record.file"
              :href="record.file.url"
              :download="record.file.filename"
              target="_blank"
              rel="noopener noreferrer"
            >
              <NextButton
                v-tooltip.top-end="$t('LIBRARIES.RECORDS_TOOL.DOWNLOAD_FILE')"
                icon="i-lucide-download"
                md
                slate
              />
            </a>
            <a
              v-else-if="record.source"
              :href="record.source"
              target="_blank"
              rel="noopener noreferrer"
            >
              <NextButton
                v-tooltip.top-end="$t('LIBRARIES.RECORDS_TOOL.OPEN_URL')"
                icon="i-lucide-external-link"
                md
                slate
              />
            </a>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="pagination.hasMore" class="flex justify-center py-2">
      <NextButton
        :label="$t('LIBRARIES.RECORDS_TOOL.LOAD_MORE')"
        md
        slate
        :is-loading="uiFlags.isLoadingMore"
        @click="loadMore"
      />
    </div>
  </div>
</template>
